import { useState, FormEvent, useCallback, useEffect } from "react";
import Navbar from "../../components/layout/Navbar";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/Button";
import { apiClient } from "../../services/apiClient";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectRefreshToken,
  selectToken,
} from "../../features/authSlice";
import { debounce } from "../../utils/debounce";
import { toast } from "sonner";
import { selectGlobalData } from "../../features/appSlice";
import { useRef } from "react";
import { ChevronDown, Search, Banknote, Loader2 } from "lucide-react";
import { redirectToDashboard } from "../../utils/dashboardRedirect";

const StoreSetup = () => {
  const token = useSelector(selectToken);
  const refreshToken = useSelector(selectRefreshToken);
  const user = useSelector(selectCurrentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: searchParams.get("name") || "",
    slug: "",
    country: "",
    currency: "",
  });
  const { countries, currencies, userLocation, status } =
    useSelector(selectGlobalData);
  const [country_code, setCountryCode] = useState(userLocation?.country_code);
  const [error, setError] = useState("");
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<
    "country" | "currency" | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Create a debounced version of the check availability function
  const debouncedCheckAvailability = useCallback(
    debounce((slug: string) => {
      if (slug) {
        checkAvailability(slug);
      } else {
        setSlugAvailable(null);
        setError("");
      }
    }, 500) as (slug: string) => void,
    [token]
  );

  // Function to check slug availability
  const checkAvailability = async (slug: string) => {
    if (!slug) {
      setSlugAvailable(null);
      return;
    }

    setIsChecking(true);
    try {
      const result = await apiClient.post<{
        status: string;
        available: boolean;
      }>("/slug-available", { slug }, { Authorization: `Bearer ${token}` });

      setSlugAvailable(result.available);
      if (!result.available) {
        setError("This domain is already taken. Please try another one.");
      } else {
        setError("");
      }
    } catch (err) {
      console.error("Failed to check domain availability:", err);
      setError("Error checking domain availability. Please try again.");
      setSlugAvailable(null);
    } finally {
      setIsChecking(false);
    }
  };

  const validateSlug = (slug: string) => {
    const slugRegex = /^[-a-zA-Z0-9_]+$/;
    return slugRegex.test(slug);
  };

  // Auto fill country and currency from user location
  useEffect(() => {
    if (status === "succeeded" && userLocation && !formData.country) {
      const matchedCountry = countries.find(
        (c) => c.name === userLocation.country
      );

      if (matchedCountry) {
        setFormData((prev) => ({
          ...prev,
          country: matchedCountry.name,
          currency: matchedCountry.currency,
        }));
        setCountryCode(matchedCountry.country_code);
      }
    }
  }, [status, userLocation, countries]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "country") {
      const selectedCountry = countries.find((c) => c.name === value);
      setCountryCode(selectedCountry?.country_code || "");
      if (selectedCountry) {
        setFormData((prev) => ({
          ...prev,
          country: value,
          currency: selectedCountry.currency,
        }));
        return;
      }
    }
    // Reset availability status when editing the slug
    if (name === "slug") {
      if (value !== "" && !validateSlug(value)) {
        return;
      }
      setSlugAvailable(null);
      // Trigger debounced check if the slug has at least 2 characters
      if (value.length >= 2 && validateSlug(value)) {
        debouncedCheckAvailability(value);
      } else {
        setSlugAvailable(null);
        setError("");
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // // Manual check availability function (for the button)
  // const handleCheckAvailability = () => {
  //   if (!formData.slug) {
  //     setError("Please enter a store domain");
  //     return;
  //   }

  //   checkAvailability(formData.slug);
  // };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!formData.name || !formData.slug) {
      setError("Please fill in all required fields");
      setIsLoading(false);
      return;
    }

    try {
      // Check slug availability again before submitting
      // const availabilityCheck = await checkSite({
      //   slug: formData.slug,
      //   access: token || "",
      // }).unwrap();

      // if (!availabilityCheck.available) {
      //   setError("This domain is already taken. Please try another one.");
      //   return;
      // }
      console.log(country_code);
      const result = await apiClient.post<{
        status: string;
        available: boolean;
      }>(
        "/create-business-site",
        {
          name: formData.name,
          slug: formData.slug.toLowerCase(),
          country: formData.country,
          currency: formData.currency,
          country_code: country_code,
        },
        {
          Authorization: `Bearer ${token}`,
        }
      );

      if (error) {
        throw error;
      }
      console.log("Site created:", result);

      toast.success("Store created successfully!", {
        description: "Redirecting to your store...",
      });

      await redirectToDashboard({
        path: "setup",
        slug: formData.slug,
        fallbackAccessToken: token,
        fallbackRefreshToken: refreshToken,
      });
    } catch (error: unknown) {
      if (typeof error === "object" && error !== null && "data" in error) {
        const err = error as { data?: Record<string, string[]> };
        toast.error(
          Object.entries(err.data as Record<string, string[]>)
            .map(([_, value]) => value)
            .join(", ") || "Failed to create store"
        );
      }
      console.error("Failed to create store:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
        setSearchQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderDropdownList = (type: "country" | "currency") => {
    if (activeDropdown !== type) return null;

    let items: any[] = [];
    if (type === "country") {
      items = countries.filter((c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    } else {
      items = currencies.filter((c) =>
        c.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return (
      <div className="absolute top-[55px] left-0 w-full min-w-[280px] bg-white border border-[#E4E7EC] rounded-xl shadow-lg z-50 overflow-hidden">
        {/* Search Input */}
        <div className="p-3 border-b border-gray-100 bg-[#F9FAFB]">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              autoFocus
              type="text"
              placeholder="Search..."
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="max-h-[250px] overflow-y-auto">
          {items.length > 0 ? (
            items.map((item, idx) => {
              const label = type === "country" ? item.name : item; // Handle object vs string
              return (
                <div
                  key={idx}
                  className="px-4 py-2 hover:bg-purple-50 cursor-pointer flex items-center gap-3 text-sm text-gray-700 transition-colors"
                  onClick={() => {
                    const syntheticEvent = {
                      target: { name: type, value: label },
                    };
                    handleChange(syntheticEvent as any);
                    setActiveDropdown(null);
                    setSearchQuery("");
                  }}
                >
                  {type === "country" ? (
                    <span className="truncate">{label}</span>
                  ) : (
                    <span className="font-mono font-medium">{label}</span>
                  )}
                </div>
              );
            })
          ) : (
            <div className="p-4 text-center text-sm text-gray-400">
              No results found
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <Navbar />
      <div className="p-5 h-[81vh] sm:p-14 flex items-start md:items-center justify-center landing-bg bg-cover bg-no-repeat bg-center">
        <div className="w-full md:w-fit flex items-center justify-center flex-col bg-white">
          <div className=" sm:p-10 rounded-2xl sm:border border-[#7E57C233]  w-[100%] sm:w-[80%] md:w-fit flex items-center justify-between gap-20 ">
            <div className="flex flex-col gap-6 items-start justify-start w-[500px]">
              <h2 className="">Create your store</h2>
              {error && <p className="text-red-500">{error}</p>}
              <form
                className="flex flex-col items-start justify-center gap-5 w-full"
                onSubmit={handleSubmit}
              >
                <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                  <label htmlFor="name" className="text-[#666666CC]">
                    Store Name
                  </label>
                 <input
                    type="text"
                    name="name"
                    id="name"
                    placeholder="e.g Ella's Collections"
                    value={formData.name}
                    onChange={handleChange}
                    className="h-11 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                    maxLength={30}
                    required
                  />
                  <span className={`text-xs self-end ${formData.name.length >= 30 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                    {formData.name.length}/30
                  </span>
                </div>
                <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                  <label htmlFor="slug" className="text-[#666666CC]">
                    Store Domain (Website name)
                  </label>
                  <div className="relative w-full">
                    {" "}
                    <div
                      className={`h-11 w-full border border-[#66666659] rounded-lg outline-0 pr-3 pl-1 flex items-center justify-between ${
                        formData.slug.length >= 2
                          ? slugAvailable === true
                            ? "border-green-500"
                            : slugAvailable === false
                              ? "border-red-500"
                              : ""
                          : ""
                      }`}
                    >
                      <input
                        type="text"
                        name="slug"
                        id="slug"
                        value={formData.slug}
                        onChange={handleChange}
                        className="w-full outline-0 h-full text-lg"
                        placeholder="shopwithella"
                        maxLength={63}
                        required
                      />
                      <span className="text-lg">.jellosite.store</span>
                    </div>
                    <div className="flex items-center text-sm mt-1 h-6">
                      {isChecking && (
                        <span className="text-gray-500">
                          Checking availability...
                        </span>
                      )}
                      {!isChecking && slugAvailable === true && (
                        <span className="text-green-500 flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Slug available
                        </span>
                      )}
                      {!isChecking && slugAvailable === false && (
                        <span className="text-red-500 flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Slug not available
                        </span>
                      )}
                    </div>
                  </div>
                  {/* <button
                    type="button"
                    onClick={handleCheckAvailability}
                    className="text-[#7E57C2] text-sm mt-2"
                    disabled={isChecking || formData.slug.length === 0}
                  >
                    Check manually
                  </button> */}
                </div>
                <div className="flex gap-4 w-full" ref={dropdownRef}>
                  {/* Country Field */}
                  <div className="flex items-start justify-center flex-col gap-1.5 w-1/2 relative">
                    <label className="text-[#666666CC]">Country</label>
                    <div
                      onClick={() => {
                        if (status !== "loading") {
                          setActiveDropdown(
                            activeDropdown === "country" ? null : "country"
                          );
                          setSearchQuery("");
                        }
                      }}
                      className={`flex items-center justify-between px-3 h-11 w-full border border-[#E4E7EC] rounded-[15px] bg-white cursor-pointer hover:bg-gray-50 transition-colors ${status === "loading" ? "opacity-50" : ""}`}
                    >
                      {status === "loading" ? (
                        <Loader2
                          size={16}
                          className="animate-spin text-gray-400 mx-auto"
                        />
                      ) : (
                        <div className="flex items-center gap-2 overflow-hidden">
                          <span className="text-[#3D3D3D] text-sm truncate">
                            {formData.country || "Select Country"}
                          </span>
                        </div>
                      )}
                      <ChevronDown
                        size={14}
                        className="text-[#3D3D3D] shrink-0"
                      />
                    </div>
                    {renderDropdownList("country")}
                  </div>

                  {/* Currency Field */}
                  <div className="flex items-start justify-center flex-col gap-1.5 w-1/2 relative">
                    <label className="text-[#666666CC]">Currency</label>
                    <div
                      className="flex items-center h-11 w-full"
                      onClick={() => {
                        if (status !== "loading") {
                          setActiveDropdown(
                            activeDropdown === "currency" ? null : "currency"
                          );
                          setSearchQuery("");
                        }
                      }}
                    >
                      {/* Icon Box */}
                      <div className="relative z-10 w-[46px] h-11 bg-[#F2F4F7] border border-[#E4E7EC] rounded-[15px] flex items-center justify-center shrink-0">
                        <Banknote size={18} className="text-[#3D3D3D]" />
                      </div>

                      {/* Text Box */}
                      <div className="relative -ml-[20px] pl-[26px] pr-3 h-full bg-white border border-[#E4E7EC] rounded-r-[15px] flex items-center justify-between gap-1 hover:bg-gray-50 transition-colors rounded-l-none border-l-0 w-full min-w-0 cursor-pointer">
                        <span className="text-[#3D3D3D] text-sm truncate">
                          {formData.currency || "---"}
                        </span>
                        <ChevronDown
                          size={14}
                          className="text-[#3D3D3D] shrink-0"
                        />
                      </div>
                    </div>
                    {renderDropdownList("currency")}
                  </div>
                </div>
                <p className="text-[11px] text-amber-600 leading-tight">
                  Note: Currency is permanent and cannot be changed later.
                </p>
                <Button
                  text={isLoading ? "Creating Store..." : "Create Store"}
                  disabled={
                    isLoading || isChecking || !validateSlug(formData.slug)
                  }
                  className="w-full disabled:opacity-50 sm:w-fit"
                />
              </form>
            </div>
            <img
              src="/assets/Jellosite-Logo-09.jpg"
              alt=""
              className="w-[250px] hidden md:block"
            />
          </div>
          <ul className="text-[#666666] md:w-full hidden sm:flex gap-3.5 sm:w-[80%]  items-center justify-end mt-1 ">
            <Link to="">
              <li>Help</li>
            </Link>
            <Link to="">
              <li>Privacy</li>
            </Link>
            <Link to="">
              <li>Terms</li>
            </Link>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StoreSetup;
